"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useProjectContext = exports.ProjectProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const api_1 = require("../services/api");
const initialState = {
    projects: [],
    currentProject: null,
    loading: false,
    error: null,
    projectCache: new Map(),
};
function projectReducer(state, action) {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'SET_PROJECTS':
            return { ...state, projects: action.payload };
        case 'SET_CURRENT_PROJECT':
            return { ...state, currentProject: action.payload };
        case 'UPDATE_PROJECT':
            return {
                ...state,
                currentProject: action.payload,
                projects: state.projects.map(p => p.project_id === action.payload.project_id
                    ? { ...p, ...action.payload }
                    : p),
            };
        case 'ADD_PROJECT':
            return { ...state, projects: [action.payload, ...state.projects] };
        case 'REMOVE_PROJECT':
            return {
                ...state,
                projects: state.projects.filter(p => p.project_id !== action.payload),
                currentProject: state.currentProject?.project_id === action.payload
                    ? null
                    : state.currentProject,
            };
        case 'UPDATE_PROJECT_SPELL_CHECK':
            return {
                ...state,
                projects: state.projects.map(p => p.project_id === action.payload.projectId
                    ? { ...p, spellCheckSummary: action.payload.summary }
                    : p),
                currentProject: state.currentProject?.project_id === action.payload.projectId && action.payload.segments
                    ? { ...state.currentProject, segments: action.payload.segments }
                    : state.currentProject,
            };
        case 'UPDATE_PROJECT_CACHE':
            const newCache = new Map(state.projectCache);
            newCache.set(action.payload.projectId, action.payload.cache);
            return {
                ...state,
                projectCache: newCache,
            };
        case 'CLEAR_PROJECT_CACHE':
            if (action.payload) {
                // Clear specific project cache
                const clearedCache = new Map(state.projectCache);
                clearedCache.delete(action.payload);
                return {
                    ...state,
                    projectCache: clearedCache,
                };
            }
            else {
                // Clear all cache
                return {
                    ...state,
                    projectCache: new Map(),
                };
            }
        default:
            return state;
    }
}
const ProjectContext = (0, react_1.createContext)(undefined);
const ProjectProvider = ({ children, }) => {
    const [state, dispatch] = (0, react_1.useReducer)(projectReducer, initialState);
    const loadProjects = (0, react_1.useCallback)(async () => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const projects = await api_1.projectsApi.getProjects();
            dispatch({ type: 'SET_PROJECTS', payload: projects });
        }
        catch (error) {
            dispatch({ type: 'SET_ERROR', payload: 'Failed to load projects' });
        }
        finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);
    // Cache timeout in milliseconds (30 seconds)
    const CACHE_TIMEOUT = 30000;
    const loadProject = (0, react_1.useCallback)(async (projectId, forceRefresh = false) => {
        // Check cache first
        const cached = state.projectCache.get(projectId);
        const now = Date.now();
        if (!forceRefresh && cached && (now - cached.lastUpdated) < CACHE_TIMEOUT) {
            // Use cached data
            dispatch({ type: 'SET_CURRENT_PROJECT', payload: cached.project });
            return;
        }
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const project = await api_1.projectsApi.getProject(projectId);
            const sources = await api_1.projectsApi.getProjectSources(projectId);
            // Update cache
            const newCache = new Map(state.projectCache);
            newCache.set(projectId, {
                project,
                sources,
                lastUpdated: now
            });
            dispatch({ type: 'UPDATE_PROJECT_CACHE', payload: { projectId, cache: { project, sources, lastUpdated: now } } });
            dispatch({ type: 'SET_CURRENT_PROJECT', payload: project });
        }
        catch (error) {
            dispatch({ type: 'SET_ERROR', payload: 'Failed to load project' });
        }
        finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [state.projectCache]);
    const createProject = async (youtubeUrl) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const project = await api_1.projectsApi.createProject(youtubeUrl);
            dispatch({ type: 'ADD_PROJECT', payload: project });
            dispatch({ type: 'SET_CURRENT_PROJECT', payload: project });
        }
        catch (error) {
            dispatch({ type: 'SET_ERROR', payload: 'Failed to create project' });
            throw error;
        }
        finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };
    const updateSegment = async (segmentId, correctedText) => {
        if (!state.currentProject)
            return;
        try {
            const updatedSegment = await api_1.projectsApi.updateSegment(state.currentProject.project_id, segmentId, correctedText);
            const updatedProject = {
                ...state.currentProject,
                segments: state.currentProject.segments.map(segment => segment.segment_id === segmentId ? {
                    ...segment,
                    ...updatedSegment,
                    // Ujisti se, že FE filtry mají data hned: pokud backend nevrací některé nové fieldy, doplníme je
                    manual_edit_count: updatedSegment.manual_edit_count ?? (segment.manual_edit_count || 1),
                    last_manual_edit_at: updatedSegment.last_manual_edit_at ?? new Date().toISOString(),
                } : segment),
            };
            dispatch({ type: 'UPDATE_PROJECT', payload: updatedProject });
        }
        catch (error) {
            dispatch({ type: 'SET_ERROR', payload: 'Failed to update segment' });
            throw error;
        }
    };
    const deleteProject = async (projectId) => {
        try {
            await api_1.projectsApi.deleteProject(projectId);
            dispatch({ type: 'REMOVE_PROJECT', payload: projectId });
        }
        catch (error) {
            dispatch({ type: 'SET_ERROR', payload: 'Failed to delete project' });
            throw error;
        }
    };
    const refreshCurrentProject = async () => {
        if (!state.currentProject)
            return;
        // Force refresh to bypass cache so UI reflects latest status and segments immediately
        await loadProject(state.currentProject.project_id, true);
    };
    const updateProjectSpellCheck = (projectId, summary, segments) => {
        dispatch({
            type: 'UPDATE_PROJECT_SPELL_CHECK',
            payload: { projectId, summary, segments }
        });
    };
    // Intelligent auto-refresh - only when user is not actively using the app
    const lastUserActivity = (0, react_1.useRef)(Date.now());
    (0, react_1.useEffect)(() => {
        loadProjects();
        // Track user activity
        const updateActivity = () => {
            lastUserActivity.current = Date.now();
        };
        document.addEventListener('mousedown', updateActivity);
        document.addEventListener('keydown', updateActivity);
        document.addEventListener('scroll', updateActivity);
        // Smart refresh interval
        const scheduleRefresh = () => {
            const timeSinceActivity = Date.now() - lastUserActivity.current;
            const isUserIdle = timeSinceActivity > 5000; // 5 seconds of inactivity
            const isDocumentVisible = !document.hidden;
            if (isUserIdle && isDocumentVisible) {
                loadProjects();
            }
            setTimeout(scheduleRefresh, 10000); // Check every 10 seconds
        };
        const refreshTimer = setTimeout(scheduleRefresh, 10000);
        return () => {
            clearTimeout(refreshTimer);
            document.removeEventListener('mousedown', updateActivity);
            document.removeEventListener('keydown', updateActivity);
            document.removeEventListener('scroll', updateActivity);
        };
    }, [loadProjects]);
    return ((0, jsx_runtime_1.jsx)(ProjectContext.Provider, { value: {
            state,
            loadProjects,
            loadProject,
            createProject,
            updateSegment,
            deleteProject,
            refreshCurrentProject,
            updateProjectSpellCheck,
        }, children: children }));
};
exports.ProjectProvider = ProjectProvider;
const useProjectContext = () => {
    const context = (0, react_1.useContext)(ProjectContext);
    if (!context) {
        throw new Error('useProjectContext must be used within a ProjectProvider');
    }
    return context;
};
exports.useProjectContext = useProjectContext;
